import { useEffect, useRef, useState } from 'react';

// ── Utility hook: fires once when element enters viewport ──────────────────
function useInView(threshold = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ── 1. Hero floating botanical lines (absolute overlay) ───────────────────
export function HeroDrawingOverlay() {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setDrawn(true), 800); return () => clearTimeout(t); }, []);

  const dur = (s: number) => `${s}s`;
  const style = (delay: number, duration: number): React.CSSProperties => ({
    strokeDasharray: 1000,
    strokeDashoffset: drawn ? 0 : 1000,
    transition: `stroke-dashoffset ${dur(duration)} cubic-bezier(0.4,0,0.2,1) ${delay}s`,
  });

  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      <svg
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
        fill="none"
      >
        {/* Top-right botanical branch */}
        <path
          d="M1200 0 C1250 80, 1320 120, 1380 200 C1400 230, 1420 260, 1440 300"
          stroke="rgba(201,169,110,0.25)" strokeWidth="0.8"
          style={style(1.0, 2.5)}
        />
        <path
          d="M1300 50 C1330 90, 1310 140, 1350 170"
          stroke="rgba(201,169,110,0.18)" strokeWidth="0.6"
          style={style(1.5, 1.8)}
        />
        <path
          d="M1350 30 C1370 70, 1360 110, 1400 130"
          stroke="rgba(201,169,110,0.15)" strokeWidth="0.5"
          style={style(1.8, 1.5)}
        />
        {/* Leaf shapes top-right */}
        <path
          d="M1280 80 C1295 60, 1320 65, 1310 90 C1300 115, 1275 105, 1280 80Z"
          stroke="rgba(201,169,110,0.2)" strokeWidth="0.6"
          style={style(2.0, 1.2)}
        />
        <path
          d="M1340 110 C1355 90, 1378 95, 1368 118 C1358 141, 1335 132, 1340 110Z"
          stroke="rgba(201,169,110,0.18)" strokeWidth="0.5"
          style={style(2.3, 1.2)}
        />

        {/* Bottom-left flowing lines */}
        <path
          d="M0 700 C60 680, 100 720, 150 700 C200 680, 240 710, 280 690"
          stroke="rgba(255,255,255,0.12)" strokeWidth="0.7"
          style={style(1.2, 2.0)}
        />
        <path
          d="M0 750 C80 730, 140 760, 200 740 C260 720, 300 750, 360 730"
          stroke="rgba(255,255,255,0.08)" strokeWidth="0.5"
          style={style(1.6, 2.2)}
        />



        {/* Floating dots */}
        {[[1100, 200], [1250, 350], [1380, 150], [200, 650], [100, 800]].map(([cx, cy], i) => (
          <circle
            key={i} cx={cx} cy={cy} r="1.5"
            fill="rgba(201,169,110,0.4)"
            style={{ opacity: drawn ? 1 : 0, transition: `opacity 0.5s ease ${1.5 + i * 0.2}s` }}
          />
        ))}
      </svg>
    </div>
  );
}

// ── 2. Section divider — animated gold line with center ornament ──────────
export function GoldLineDivider({ delay = 0 }: { delay?: number }) {
  const { ref, inView } = useInView(0);
  return (
    <div ref={ref} className="w-full flex items-center justify-center gap-4 py-2 overflow-hidden">
      <div
        style={{
          height: '1px',
          background: 'linear-gradient(to right, transparent, #C9A96E)',
          width: inView ? '120px' : '0px',
          transition: `width 1.2s cubic-bezier(0.4,0,0.2,1) ${delay}s`,
        }}
      />
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 1 L8 15 M1 8 L15 8"
          stroke="#C9A96E"
          strokeWidth="0.8"
          style={{
            strokeDasharray: 30,
            strokeDashoffset: inView ? 0 : 30,
            transition: `stroke-dashoffset 0.8s ease ${delay + 0.6}s`,
          }}
        />
        <circle
          cx="8" cy="8" r="2.5"
          stroke="#C9A96E" strokeWidth="0.8" fill="none"
          style={{
            strokeDasharray: 20,
            strokeDashoffset: inView ? 0 : 20,
            transition: `stroke-dashoffset 0.8s ease ${delay + 0.9}s`,
          }}
        />
      </svg>
      <div
        style={{
          height: '1px',
          background: 'linear-gradient(to left, transparent, #C9A96E)',
          width: inView ? '120px' : '0px',
          transition: `width 1.2s cubic-bezier(0.4,0,0.2,1) ${delay}s`,
        }}
      />
    </div>
  );
}

// ── 3. Botanical corner ornament (for section backgrounds) ────────────────
export function BotanicalCorner({
  position = 'top-right',
  color = 'rgba(201,169,110,0.15)',
  size = 200,
}: {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  color?: string;
  size?: number;
}) {
  const { ref, inView } = useInView(0);
  const flip = {
    'top-right': 'top-0 right-0',
    'top-left': 'top-0 left-0 scale-x-[-1]',
    'bottom-right': 'bottom-0 right-0 scale-y-[-1]',
    'bottom-left': 'bottom-0 left-0 scale-x-[-1] scale-y-[-1]',
  }[position];

  const pathStyle = (delay: number, len = 300): React.CSSProperties => ({
    strokeDasharray: len,
    strokeDashoffset: inView ? 0 : len,
    transition: `stroke-dashoffset 2s cubic-bezier(0.4,0,0.2,1) ${delay}s`,
  });

  return (
    <div ref={ref} className={`absolute ${flip} pointer-events-none overflow-hidden`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 200 200" width={size} height={size} fill="none">
        {/* Main stem */}
        <path d="M200 0 C160 40, 120 80, 80 140 C60 170, 40 185, 0 200"
          stroke={color} strokeWidth="1" style={pathStyle(0, 400)} />
        {/* Branch 1 */}
        <path d="M150 50 C130 30, 110 35, 100 60"
          stroke={color} strokeWidth="0.7" style={pathStyle(0.4, 150)} />
        {/* Branch 2 */}
        <path d="M120 90 C100 70, 80 75, 70 100"
          stroke={color} strokeWidth="0.7" style={pathStyle(0.7, 150)} />
        {/* Branch 3 */}
        <path d="M90 130 C70 110, 50 115, 40 140"
          stroke={color} strokeWidth="0.6" style={pathStyle(1.0, 130)} />
        {/* Leaf 1 */}
        <path d="M130 40 C145 25, 165 30, 155 50 C145 70, 125 60, 130 40Z"
          stroke={color} strokeWidth="0.6" style={pathStyle(0.6, 100)} />
        {/* Leaf 2 */}
        <path d="M100 70 C115 55, 135 60, 125 80 C115 100, 95 90, 100 70Z"
          stroke={color} strokeWidth="0.6" style={pathStyle(0.9, 100)} />
        {/* Leaf 3 */}
        <path d="M68 108 C83 93, 103 98, 93 118 C83 138, 63 128, 68 108Z"
          stroke={color} strokeWidth="0.5" style={pathStyle(1.2, 100)} />
        {/* Small dots */}
        {[[170, 20], [140, 55], [108, 88], [75, 125]].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="1.5" fill={color}
            style={{ opacity: inView ? 1 : 0, transition: `opacity 0.4s ease ${0.8 + i * 0.15}s` }} />
        ))}
      </svg>
    </div>
  );
}

// ── 4. Flowing wave lines (for dark sections) ─────────────────────────────
export function FlowingWaves({
  color = 'rgba(201,169,110,0.12)',
  lineCount = 4,
}: {
  color?: string;
  lineCount?: number;
}) {
  const { ref, inView } = useInView(0);
  const waves = Array.from({ length: lineCount }, (_, i) => ({
    y: 30 + i * 22,
    delay: i * 0.3,
    amplitude: 12 + i * 4,
    phase: i * 0.5,
  }));

  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none overflow-hidden">
      <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="w-full h-full" fill="none">
        {waves.map((w, i) => {
          const points = Array.from({ length: 20 }, (_, j) => {
            const x = (j / 19) * 1440;
            const y = w.y + Math.sin((j / 19) * Math.PI * 3 + w.phase) * w.amplitude;
            return `${j === 0 ? 'M' : 'L'}${x},${y}`;
          }).join(' ');
          return (
            <path
              key={i}
              d={points}
              stroke={color}
              strokeWidth={0.8 - i * 0.1}
              style={{
                strokeDasharray: 2000,
                strokeDashoffset: inView ? 0 : 2000,
                transition: `stroke-dashoffset 3s cubic-bezier(0.4,0,0.2,1) ${w.delay}s`,
              }}
            />
          );
        })}
      </svg>
    </div>
  );
}

// ── 5. Circular mandala ornament ──────────────────────────────────────────
export function MandalaOrnament({
  size = 120,
  color = 'rgba(201,169,110,0.2)',
  delay = 0,
}: {
  size?: number;
  color?: string;
  delay?: number;
}) {
  const { ref, inView } = useInView(0);
  const c = size / 2;
  const r1 = c * 0.85;
  const r2 = c * 0.65;
  const r3 = c * 0.4;
  const circ = (r: number) => 2 * Math.PI * r;

  const circStyle = (r: number, d: number): React.CSSProperties => ({
    strokeDasharray: circ(r),
    strokeDashoffset: inView ? 0 : circ(r),
    transition: `stroke-dashoffset 2s cubic-bezier(0.4,0,0.2,1) ${delay + d}s`,
  });

  const lineStyle = (d: number): React.CSSProperties => ({
    strokeDasharray: 100,
    strokeDashoffset: inView ? 0 : 100,
    transition: `stroke-dashoffset 1s ease ${delay + d}s`,
  });

  return (
    <div ref={ref} style={{ width: size, height: size, flexShrink: 0 }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} fill="none">
        <circle cx={c} cy={c} r={r1} stroke={color} strokeWidth="0.6" style={circStyle(r1, 0)} />
        <circle cx={c} cy={c} r={r2} stroke={color} strokeWidth="0.5" style={circStyle(r2, 0.3)} />
        <circle cx={c} cy={c} r={r3} stroke={color} strokeWidth="0.5" style={circStyle(r3, 0.6)} />
        {/* 8 radial lines */}
        {Array.from({ length: 8 }, (_, i) => {
          const angle = (i / 8) * Math.PI * 2;
          const x1 = c + Math.cos(angle) * r3;
          const y1 = c + Math.sin(angle) * r3;
          const x2 = c + Math.cos(angle) * r1;
          const y2 = c + Math.sin(angle) * r1;
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={color} strokeWidth="0.5"
              style={lineStyle(0.9 + i * 0.05)} />
          );
        })}
        {/* Center dot */}
        <circle cx={c} cy={c} r="2" fill={color}
          style={{ opacity: inView ? 1 : 0, transition: `opacity 0.5s ease ${delay + 1.2}s` }} />
      </svg>
    </div>
  );
}

// ── 6. Vertical side line with dots ──────────────────────────────────────
export function VerticalAccentLine({
  side = 'left',
  color = '#C9A96E',
  height = 200,
}: {
  side?: 'left' | 'right';
  color?: string;
  height?: number;
}) {
  const { ref, inView } = useInView(0);
  return (
    <div
      ref={ref}
      className={`absolute top-1/2 -translate-y-1/2 ${side === 'left' ? 'left-6 md:left-10' : 'right-6 md:right-10'} pointer-events-none`}
    >
      <svg width="12" height={height} viewBox={`0 0 12 ${height}`} fill="none">
        <line
          x1="6" y1="0" x2="6" y2={height}
          stroke={color} strokeWidth="0.6" opacity="0.3"
          style={{
            strokeDasharray: height,
            strokeDashoffset: inView ? 0 : height,
            transition: 'stroke-dashoffset 1.5s ease 0.2s',
          }}
        />
        {[0.2, 0.5, 0.8].map((ratio, i) => (
          <circle
            key={i} cx="6" cy={height * ratio} r="2"
            fill="none" stroke={color} strokeWidth="0.8" opacity="0.5"
            style={{
              strokeDasharray: 15,
              strokeDashoffset: inView ? 0 : 15,
              transition: `stroke-dashoffset 0.6s ease ${0.5 + i * 0.2}s`,
            }}
          />
        ))}
      </svg>
    </div>
  );
}

// ── 7. Tattoo needle path drawing animation ───────────────────────────────
export function TattooPathDrawing({
  color = 'rgba(201,169,110,0.35)',
  width = 300,
  height = 200,
}: {
  color?: string;
  width?: number;
  height?: number;
}) {
  const { ref, inView } = useInView(0);

  const pathStyle = (delay: number, len = 500): React.CSSProperties => ({
    strokeDasharray: len,
    strokeDashoffset: inView ? 0 : len,
    transition: `stroke-dashoffset 2.5s cubic-bezier(0.4,0,0.2,1) ${delay}s`,
  });

  return (
    <div ref={ref} style={{ width, height, flexShrink: 0 }}>
      <svg viewBox="0 0 300 200" width={width} height={height} fill="none">
        {/* Rose outline */}
        <path
          d="M150 160 C130 140, 100 120, 110 90 C120 60, 150 50, 150 50 C150 50, 180 60, 190 90 C200 120, 170 140, 150 160Z"
          stroke={color} strokeWidth="1"
          style={pathStyle(0, 400)}
        />
        {/* Inner petal */}
        <path
          d="M150 140 C138 125, 122 112, 128 95 C134 78, 150 72, 150 72 C150 72, 166 78, 172 95 C178 112, 162 125, 150 140Z"
          stroke={color} strokeWidth="0.7"
          style={pathStyle(0.5, 300)}
        />
        {/* Stem */}
        <path
          d="M150 160 C148 175, 145 185, 143 200"
          stroke={color} strokeWidth="0.8"
          style={pathStyle(1.0, 100)}
        />
        {/* Left leaf */}
        <path
          d="M147 175 C135 165, 118 168, 120 180 C122 192, 140 190, 147 175Z"
          stroke={color} strokeWidth="0.6"
          style={pathStyle(1.3, 120)}
        />
        {/* Right leaf */}
        <path
          d="M145 182 C157 172, 174 175, 172 187 C170 199, 152 197, 145 182Z"
          stroke={color} strokeWidth="0.6"
          style={pathStyle(1.6, 120)}
        />
        {/* Decorative dots */}
        {[[110, 85], [190, 85], [150, 48], [130, 155], [170, 155]].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="1.5" fill={color}
            style={{ opacity: inView ? 1 : 0, transition: `opacity 0.4s ease ${1.8 + i * 0.1}s` }} />
        ))}
        {/* Fine detail lines */}
        <path
          d="M135 100 C140 95, 145 98, 150 95 C155 92, 160 95, 165 100"
          stroke={color} strokeWidth="0.5"
          style={pathStyle(0.8, 80)}
        />
        <path
          d="M132 110 C138 105, 144 108, 150 105 C156 102, 162 105, 168 110"
          stroke={color} strokeWidth="0.5"
          style={pathStyle(1.0, 80)}
        />
      </svg>
    </div>
  );
}

// ── 8. Horizontal decorative rule ─────────────────────────────────────────
export function DecorativeRule({ color = '#C9A96E', delay = 0 }: { color?: string; delay?: number }) {
  const { ref, inView } = useInView(0);
  return (
    <div ref={ref} className="flex items-center gap-3 w-full overflow-hidden">
      <div style={{
        height: '1px', background: color, opacity: 0.25,
        width: inView ? '100%' : '0%',
        transition: `width 1.5s cubic-bezier(0.4,0,0.2,1) ${delay}s`,
      }} />
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
        <path d="M10 2 L10 18 M2 10 L18 10"
          stroke={color} strokeWidth="0.8" opacity="0.5"
          style={{
            strokeDasharray: 40,
            strokeDashoffset: inView ? 0 : 40,
            transition: `stroke-dashoffset 0.8s ease ${delay + 0.8}s`,
          }}
        />
      </svg>
      <div style={{
        height: '1px', background: color, opacity: 0.25,
        width: inView ? '100%' : '0%',
        transition: `width 1.5s cubic-bezier(0.4,0,0.2,1) ${delay}s`,
      }} />
    </div>
  );
}
